import React, { Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { LoadingSpinner } from './components/common/LoadingSpinner';
import { AuthInitializer } from './components/common/AuthInitializer';
import { AppBootOverlay } from './components/common/AppBootOverlay';
import { GlobalNotificationAlert } from './components/common/GlobalNotificationAlert';
import ConnectionStatusBanner from './components/common/ConnectionStatusBanner';
import SessionTimeoutProvider from './components/common/SessionTimeoutProvider';
import AuthStatusIndicator from './components/auth/AuthStatusIndicator';
import { ScrollToTop } from './components/common/ScrollToTop';
import { useNotificationWebSocket } from './hooks/useNotificationWebSocket';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { useBrandIcons } from './hooks/useBrandIcons';
import { ChatSessionProvider } from './contexts/ChatSessionContext';
import { ChatbotSessionProvider } from './contexts/ChatbotSessionContext';
import { VaultProvider } from './contexts/VaultContext';
import { ZoomProvider, useZoom } from './contexts/ZoomContext';
import { FloatingChatWidget } from './components/chat/FloatingChatWidget';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import FeatureGate from './components/access/FeatureGate';
import { fetchSiteConfiguration } from './config/siteConfig';
import { BRANDING } from './config/branding';
import './services/authInterceptor';
import './styles/urgent-animations.css';
import { useAppSelector, useAppDispatch } from './store';
import { clearPendingLogoutRedirect } from './store/authSlice';
import { LogoutCoordinator } from './services/logoutCoordinator';
import LoginPageTwoStep from './pages/LoginPageTwoStep';  // PHASE 6: Two-step login (primary)
import { LandingPage } from './pages/LandingPage';
import { AimepicCompanyLandingPage } from './pages/AimepicCompanyLandingPage';
import { OneCareLandingPage } from './pages/OneCareLandingPage';
import { PatientWelcomePage } from './pages/PatientWelcomePage';
import { EncounterNavigationPage } from './pages/EncounterNavigationPage';
import { ServiceInformationPage } from './pages/ServiceInformationPage';
import { MedicalAssistancePage } from './pages/MedicalAssistancePage';
import { PatientFeedbackPage } from './pages/PatientFeedbackPage';
import { EncounterInfoPage } from './pages/EncounterInfoPage';
import { ColorTestPage } from './pages/ColorTestPage';

// Lazy load patient feedback tracking pages
const PatientFeedbackTrackingPage = React.lazy(() => import('./pages/PatientFeedbackTrackingPage'));
const PatientTrackFeedbackPage = React.lazy(() => import('./pages/PatientTrackFeedbackPage'));

// Debug utility for development
if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
  import('./utils/debugHealthCheck');
}

// Redirects legacy /onecare/communicator* URLs to their /onecare/messages equivalent,
// preserving any query string (e.g. ?conversation=123) so deep links keep working.
const LegacyMessagesRedirect: React.FC<{ to: string }> = ({ to }) => {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
};

// Lazy load pages for better performance
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const CommunicatorPage = React.lazy(() => import('./pages/CommunicatorPage'));
const ConversationsView = React.lazy(() => import('./pages/communicator/ConversationsView'));
const StaffPatientsPage = React.lazy(() => import('./pages/StaffPatientsPage'));
const PatientRegistryPage = React.lazy(() => import('./pages/PatientRegistryPage'));
const ProjectsPage = React.lazy(() => import('./pages/ProjectsPage'));
const ProjectDashboardPage = React.lazy(() => import('./pages/ProjectDashboardPage'));
const AppointmentsPage = React.lazy(() => import('./pages/AppointmentsPage'));
const StaffDirectoryPage = React.lazy(() => import('./pages/StaffDirectoryPage'));
const ReportsPage = React.lazy(() => import('./pages/ReportsPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const AIChatPage = React.lazy(() => import('./pages/AIChatPage'));
const StaffFeedbackPage = React.lazy(() => import('./pages/StaffFeedbackPage'));
const StaffDashboardPage = React.lazy(() => import('./pages/StaffDashboardPage'));

// Notification management pages
const NotificationManagementPageWrapper = React.lazy(() => import('./pages/NotificationManagementPageWrapper'));
const NotificationTriggerConfiguration = React.lazy(() => import('./components/admin/NotificationTriggerConfiguration'));

// Admin pages
const AdminLayout = React.lazy(() => import('./components/admin/AdminLayout'));
const AdminDashboard = React.lazy(() => import('./pages/admin/AdminDashboard'));
const UserManagement = React.lazy(() => import('./pages/admin/UserManagement'));
const DutyScheduleManagement = React.lazy(() => import('./components/admin/DutyScheduleManagement'));
const SystemSettings = React.lazy(() => import('./pages/admin/SystemSettings'));
const ThemeControlPanel = React.lazy(() => import('./pages/admin/ThemeControlPanel'));
const SecurityCenter = React.lazy(() => import('./pages/admin/SecurityCenter'));
const AuditReviewPage = React.lazy(() => import('./pages/admin/AuditReviewPage'));
const AIGovernancePage = React.lazy(() => import('./pages/admin/AIGovernancePage'));
const IdentityVaultPage = React.lazy(() => import('./pages/admin/IdentityVaultPage'));
const SystemHealth = React.lazy(() => import('./pages/admin/SystemHealth'));
const HospitalManagement = React.lazy(() => import('./pages/admin/HospitalManagement'));
const AlertManagement = React.lazy(() => import('./pages/admin/AlertManagement'));
const AIPermissionControl = React.lazy(() => import('./components/admin/AIPermissionControlPage'));
const UrgencyPermissionControlPage = React.lazy(() => import('./components/admin/UrgencyPermissionControlPage'));
const PatientExperienceSettings = React.lazy(() => import('./pages/admin/PatientExperienceSettings'));
const PatientFeedbackDashboard = React.lazy(() => import('./pages/admin/PatientFeedbackDashboard'));
const FMSControlPanel = React.lazy(() => import('./pages/admin/FMSControlPanel'));
const AdminSoundControlFixed = React.lazy(() => import('./components/admin/AdminSoundControlFixed'));
const AdminRoute = React.lazy(() => import('./components/admin/AdminRoute'));
const AdminAuthGuard = React.lazy(() => import('./components/admin/AuthGuard'));
const MessagingChannelList = React.lazy(() => import('./pages/admin/MessagingChannelList'));
const MessagingChannelCreate = React.lazy(() => import('./pages/admin/MessagingChannelCreate'));
const MessagingChannelEdit = React.lazy(() => import('./pages/admin/MessagingChannelEdit'));
const MessagingChannelBulk = React.lazy(() => import('./pages/admin/MessagingChannelBulk'));
const MessagingPermissionsSuperuser = React.lazy(() => import('./pages/admin/MessagingPermissionsSuperuser'));
const MessagingPermissionsOrgAdmin = React.lazy(() => import('./pages/admin/MessagingPermissionsOrgAdmin'));
const MessagingPermissionsDomainAdmin = React.lazy(() => import('./pages/admin/MessagingPermissionsDomainAdmin'));
const PlatformAdminDashboard = React.lazy(() => import('./pages/admin/PlatformAdminDashboard'));
const HospitalCompliancePage = React.lazy(() => import('./pages/admin/HospitalCompliancePage'));
const AssistantControlPage = React.lazy(() => import('./pages/admin/AssistantControlPage'));
const GlobalOverviewPage = React.lazy(() => import('./pages/admin/GlobalOverview'));
const OrganizationFeatureAccess = React.lazy(() => import('./pages/admin/OrganizationFeatureAccess'));
const DomainFeatureAccess = React.lazy(() => import('./pages/admin/DomainFeatureAccess'));
const ToolsAIWorkspacePage = React.lazy(() => import('./pages/ToolsAIWorkspacePage'));
const ConductorPage = React.lazy(() => import('./pages/ConductorPage'));
const SmartFlowPage = React.lazy(() => import('./pages/SmartFlowPage'));
const GeriatricApp = React.lazy(() => import('./pages/geriatric/GeriatricApp'));
const CgaApp = React.lazy(() => import('./pages/cga/CgaApp'));
const CgaWriterApp = React.lazy(() => import('./pages/cga-writer/CgaWriterApp'));
const CgaSetupPage = React.lazy(() => import('./pages/admin/CgaSetupPage'));
const CgaAccessPage = React.lazy(() => import('./pages/admin/CgaAccessPage'));
const CgaWriterSettingsPage = React.lazy(
  () => import('./pages/admin/CgaWriterSettingsPage')
);
const CgaIndicatorsPage = React.lazy(() => import('./pages/admin/CgaIndicatorsPage'));
const CgaTeamRolesPage = React.lazy(() => import('./pages/admin/CgaTeamRolesPage'));
const NotesPage = React.lazy(() => import('./pages/NotesPage'));
const NotesHistoryPage = React.lazy(() => import('./pages/NotesHistoryPage'));
const ConsentPage = React.lazy(() => import('./pages/ConsentPage'));
const ConsentSettingsPage = React.lazy(() => import('./pages/admin/ConsentSettingsPage'));
const AnnouncementsPage = React.lazy(() => import('./pages/AnnouncementsPage'));
const AnnouncementAuthorsPage = React.lazy(() => import('./pages/admin/AnnouncementAuthorsPage'));
const RegistryAccessPage = React.lazy(() => import('./pages/admin/RegistryAccessPage'));
const RegistryAISetupPage = React.lazy(() => import('./pages/admin/RegistryAISetupPage'));
const ConductorAnalyticsPage = React.lazy(() => import('./pages/ConductorAnalyticsPage'));
const ConductorSettingsGate = React.lazy(() => import('./pages/admin/ConductorSettingsGate'));
const SmartFlowSettingsPage = React.lazy(() => import('./pages/admin/SmartFlowSettingsPage'));
const AIManagementPage = React.lazy(() => import('./pages/admin/AIManagementPage'));
const SmartFlowHospitalBalancesPage = React.lazy(() => import('./pages/admin/SmartFlowHospitalBalancesPage'));
const HospitalConductorBalancesPage = React.lazy(() => import('./pages/admin/HospitalConductorBalancesPage'));
// Organization & Hospital Management (Superuser + Org Admin)
const OrganizationManagementPage = React.lazy(() => import('./pages/OrganizationManagementPage'));
const HospitalManagementPage = React.lazy(() => import('./pages/HospitalManagementPage'));
const MessagingPlatformPage = React.lazy(() => import('./pages/features/MessagingPlatformPage'));
const ConductorPlatformPage = React.lazy(() => import('./pages/features/ConductorPlatformPage'));
const SmartFlowPlatformPage = React.lazy(() => import('./pages/features/SmartFlowPlatformPage'));
const PatientFeedbackPlatformPage = React.lazy(() => import('./pages/features/PatientFeedbackPlatformPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const DemoRequestPage = React.lazy(() => import('./pages/DemoRequestPage'));
const DemoSetupPage = React.lazy(() => import('./pages/DemoSetupPage'));
const DemoRequestsPage = React.lazy(() => import('./pages/admin/DemoRequestsPage'));
const SetupWizardPage = React.lazy(() => import('./pages/admin/SetupWizardPage'));
const ShiftSchedulingPage = React.lazy(() => import('./pages/ShiftSchedulingPage'));
const ShiftSchedulingPolicyPage = React.lazy(() => import('./pages/ShiftSchedulingPolicyPage'));
const OnCallSchedulerPage = React.lazy(() => import('./pages/OnCallSchedulerPage'));
const ShiftEasePage = React.lazy(() => import('./pages/ShiftEasePage'));
const EndoscopyBookingPage = React.lazy(() => import('./pages/EndoscopyBookingPage'));
const EndoscopySearchPage = React.lazy(() => import('./pages/EndoscopySearchPage'));
const EndoscopySetupPage = React.lazy(() => import('./pages/EndoscopySetupPage'));
const EndoscopyAuditPage = React.lazy(() => import('./pages/EndoscopyAuditPage'));
const Endoscopy2Page = React.lazy(() => import('./pages/endoscopy2/Endoscopy2Page'));
const Endoscopy2SetupPage = React.lazy(() => import('./pages/endoscopy2/Endoscopy2SetupPage'));
const EndoscopyReportPage = React.lazy(() => import('./pages/EndoscopyReportPage'));
const EndoscopyDischargePage = React.lazy(() => import('./pages/EndoscopyDischargePage'));
const EndoscopyDischargeSetupPage = React.lazy(() => import('./pages/EndoscopyDischargeSetupPage'));
const DataAnalysisPage = React.lazy(() => import('./pages/DataAnalysisPage'));
const TasksPage = React.lazy(() => import('./pages/TasksPage'));

/**
 * Watches for pending logout redirects from auth failure handlers and performs
 * a soft React Router navigation instead of a hard window.location.href redirect.
 * This preserves React component state during the redirect.
 */
function LogoutRedirectHandler() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const pendingRedirect = useAppSelector((state) => state.auth.pendingLogoutRedirect);

  useEffect(() => {
    if (pendingRedirect) {
      const { path, error } = pendingRedirect;
      console.warn(
        `[LOGOUT-DEBUG] LogoutRedirectHandler.redirect | reason=pending_logout_redirect | ` +
        `error=${error} | path=${path} | timestamp=${new Date().toISOString()}`
      );
      dispatch(clearPendingLogoutRedirect());
      // Reset the coordinator so future logouts can proceed
      LogoutCoordinator.reset();
      const separator = path.includes('?') ? '&' : '?';
      navigate(`${path}${separator}error=${encodeURIComponent(error)}`, { replace: true });
    }
  }, [pendingRedirect, navigate, dispatch]);

  return null;
}

function AppContent() {
  // Automatically update document title based on current route
  useDocumentTitle();
  
  // Set default page title (fallback, will be overridden by useDocumentTitle)
  useEffect(() => {
    document.title = BRANDING.meta.defaultTitle;
  }, []);
  
  // Fetch site configuration on app startup
  useEffect(() => {
    fetchSiteConfiguration().catch((error) => {
      const errorMsg = error instanceof Error 
        ? error.message 
        : (error as any)?.response?.data?.error 
        || JSON.stringify(error, null, 2);
      console.error('Failed to fetch site configuration:', errorMsg);
    });
  }, []);
  
  // Initialize notification WebSocket connection
  useNotificationWebSocket({
    autoConnect: true,
    showBrowserNotifications: true,
    showToastAlerts: true
  });

  const { zoomLevel } = useZoom();
  const location = useLocation();
  
  // Exclude login and landing pages from zoom transform (so fixed navbar works)
  const isLoginPage = location.pathname === '/onecare/login';
  const isLandingPage = location.pathname === '/' || location.pathname === '/onecare' || location.pathname === '/onecare/';
  const skipZoomTransform = isLoginPage || isLandingPage;

  // Apply zoom transform only when not login/landing (landing needs no transform for fixed header)
  const containerStyle: React.CSSProperties = skipZoomTransform
    ? {
        width: '100%',
        // Inherit the viewport height from html/body/#root, which use
        // 100dvh (with a 100vh fallback) so the bottom-pinned chat input
        // stays reachable on mobile without any fragile inline dvh here.
        height: '100%',
        position: 'relative',
      }
    : {
        transform: `scale(${zoomLevel}) translateZ(0)`,
        transformOrigin: 'top left',
        width: `${100 / zoomLevel}%`,
        height: `calc(100% / ${zoomLevel})`,
        position: 'relative',
        willChange: 'transform',
        backfaceVisibility: 'hidden',
        WebkitBackfaceVisibility: 'hidden',
        WebkitFontSmoothing: 'subpixel-antialiased',
      };

  // Tab icon, iOS home-screen icon, theme colour and the web-app manifest all
  // follow the product you are in: Aimepic on the landing pages, OneCare from
  // /onecare/login onwards. See hooks/useBrandIcons.ts.
  useBrandIcons();

  return (
    <>
    {/*
      Rendered OUTSIDE the zoom container on purpose: a CSS `transform` makes an
      element the containing block for its `position: fixed` descendants, so a
      boot overlay placed inside would be sized against the scaled container
      instead of the real viewport.
    */}
    <AppBootOverlay />
    <div style={containerStyle}>
      <div className="App" style={{ height: '100%', minHeight: '100%', width: '100%' }}>
          <LogoutRedirectHandler />
          <ScrollToTop />
          <ConnectionStatusBanner />
          <GlobalNotificationAlert />
          
          {/* Authentication status indicator for debugging/user feedback.
              Anchored bottom-right (bottom sheet on mobile) so it never covers
              page headers, branding, or the top connection banner. */}
          <div className="pointer-events-none fixed inset-x-3 bottom-3 z-[60] sm:inset-x-auto sm:right-5 sm:bottom-5 sm:w-full sm:max-w-sm">
            <div className="pointer-events-auto">
              <AuthStatusIndicator showDetails={process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true'} />
            </div>
          </div>
          <SessionTimeoutProvider>
          <VaultProvider>
          <Suspense fallback={<LoadingSpinner size="lg" className="h-screen" />}>
            <Routes>
              {/* Company Landing Page - Root */}
              <Route path="/" element={<AimepicCompanyLandingPage />} />
              
              {/* OneCare Product Routes */}
              <Route path="/onecare/" element={<OneCareLandingPage />} />
              <Route path="/onecare/login" element={<LoginPageTwoStep />} />  {/* PHASE 6: Two-step login */}
              <Route path="/onecare/color-test" element={<ColorTestPage />} />
              <Route path="/onecare/platform/messaging" element={<MessagingPlatformPage />} />
              <Route path="/onecare/platform/conductor" element={<ConductorPlatformPage />} />
              <Route path="/onecare/platform/smart-flow" element={<SmartFlowPlatformPage />} />
              <Route path="/onecare/platform/patient-feedback" element={<PatientFeedbackPlatformPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/onecare/contact" element={<ContactPage />} />
              <Route path="/demo-request" element={<DemoRequestPage />} />
              <Route path="/demo/setup/:token" element={<DemoSetupPage />} />

              {/* Staff Routes */}
              <Route path="/onecare/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
              <Route path="/onecare/messages" element={<ProtectedRoute><CommunicatorPage /></ProtectedRoute>}>
                <Route index element={<ConversationsView />} />
              </Route>
              {/* Legacy URLs → /onecare/messages (preserve query string) */}
              <Route path="/onecare/messages/inbox" element={<LegacyMessagesRedirect to="/onecare/messages" />} />
              <Route path="/onecare/communicator" element={<LegacyMessagesRedirect to="/onecare/messages" />} />
              <Route path="/onecare/communicator/conversations" element={<LegacyMessagesRedirect to="/onecare/messages" />} />
              <Route path="/onecare/ai-chat" element={<ProtectedRoute><AIChatPage /></ProtectedRoute>} />
              <Route path="/onecare/tools/conductor" element={<ProtectedRoute><ConductorPage /></ProtectedRoute>} />
              <Route path="/onecare/tools/conductor/:urlKey" element={<ProtectedRoute><ConductorPage /></ProtectedRoute>} />
              <Route path="/onecare/tools/smart-flow" element={<ProtectedRoute><SmartFlowPage /></ProtectedRoute>} />
              <Route path="/onecare/tools/smart-flow/:urlKey" element={<ProtectedRoute><SmartFlowPage /></ProtectedRoute>} />
              <Route
                path="/onecare/tools/geriatric/*"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="geriatric_smart_flow" requiredLevel="standard">
                      <GeriatricApp />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              {/* CGA Smart Guide — its OWN route tree, not nested under
                  /tools/geriatric/*, so the two features stay independently
                  switchable (that subtree is gated on geriatric_smart_flow). */}
              {/* Its own route tree, gated on its own flag: the Smart Guide
                  CAPTURES structured assessments, the Writer turns unstructured
                  notes into prose. A hospital may want either without the other. */}
              <Route
                path="/onecare/tools/cga-writer"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="cga_docgen" requiredLevel="standard">
                      <CgaWriterApp />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/cga/*"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="cga_smartguide" requiredLevel="standard">
                      <CgaApp />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/notes"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="notes" requiredLevel="standard">
                      <NotesPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/notes/history"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="notes" requiredLevel="standard">
                      <NotesHistoryPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/notes/:noteId"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="notes" requiredLevel="standard">
                      <NotesPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/consent"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="consent" requiredLevel="standard">
                      <ConsentPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/consent/settings"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="consent" requiredLevel="standard">
                      <ConsentSettingsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/consent/:consentId"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="consent" requiredLevel="standard">
                      <ConsentPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/patients"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="patient_registry" requiredLevel="standard">
                      <PatientRegistryPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/announcements"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="announcements" requiredLevel="standard">
                      <AnnouncementsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/announcements/:id"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="announcements" requiredLevel="standard">
                      <AnnouncementsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/announcement-authors"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="announcements" requiredLevel="standard">
                      <AnnouncementAuthorsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/registry-access"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="patient_registry" requiredLevel="standard">
                      <RegistryAccessPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/registry-ai-setup"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="patient_registry" requiredLevel="standard">
                      <RegistryAISetupPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/endoscopy-discharge-setup"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="endoscopy_discharge" requiredLevel="standard">
                        <EndoscopyDischargeSetupPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/endoscopy-setup"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="endoscopy_booking" requiredLevel="standard">
                        <EndoscopySetupPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              {/* Endoscopy v2 Setup: platform admins AND people granted
                  "Registry admin" in v2 — the page itself checks the
                  capability, so no AdminRoute wrapper here. */}
              <Route
                path="/onecare/admin/endoscopy2-setup"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy2" requiredLevel="standard">
                      <Endoscopy2SetupPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              {/* CGA configuration sits with every other feature's settings
                  under Admin -> Feature Modules, not inside the clinical app. */}
              <Route
                path="/onecare/admin/cga-indicators"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="cga_smartguide" requiredLevel="standard">
                        <CgaIndicatorsPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/cga-access"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="cga_smartguide" requiredLevel="standard">
                        <CgaAccessPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/cga-writer-settings"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="cga_docgen" requiredLevel="standard">
                        <CgaWriterSettingsPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              {/* The wording page was folded into the settings page above; the
                  old address is one session old but may be bookmarked. */}
              <Route
                path="/onecare/admin/cga-age-friendly-plan"
                element={<Navigate to="/onecare/admin/cga-writer-settings" replace />}
              />
              <Route
                path="/onecare/admin/cga-team-roles"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="cga_smartguide" requiredLevel="standard">
                        <CgaTeamRolesPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/cga-setup"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="cga_smartguide" requiredLevel="standard">
                        <CgaSetupPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/projects"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="project_manager" requiredLevel="standard">
                      <ProjectsPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="project_manager" requiredLevel="standard">
                      <ProjectDashboardPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/shift-scheduling/*"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="shift_scheduling" requiredLevel="read_only">
                      <ShiftSchedulingPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/admin/shift-scheduling-policy"
                element={
                  <ProtectedRoute>
                    <AdminRoute requiredLevel="hospital">
                      <FeatureGate featureCode="shift_scheduling" requiredLevel="read_only">
                        <ShiftSchedulingPolicyPage />
                      </FeatureGate>
                    </AdminRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/oncall-scheduler"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="oncall_scheduler" requiredLevel="standard">
                      <OnCallSchedulerPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/oncall-scheduler/:scheduleId"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="oncall_scheduler" requiredLevel="standard">
                      <OnCallSchedulerPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/shiftease/*"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="shiftease" requiredLevel="standard">
                      <ShiftEasePage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/endoscopy-booking"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy_booking" requiredLevel="standard">
                      <EndoscopyBookingPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/endoscopy-search"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy_booking" requiredLevel="standard">
                      <EndoscopySearchPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              {/* Setup moved into the Admin Settings panel; redirect old links. */}
              <Route
                path="/onecare/tools/endoscopy-booking/setup"
                element={<Navigate to="/onecare/admin/endoscopy-setup" replace />}
              />
              <Route
                path="/onecare/tools/endoscopy-discharge"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy_discharge" requiredLevel="standard">
                      <EndoscopyDischargePage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/endoscopy-report"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy_report" requiredLevel="standard">
                      <EndoscopyReportPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/endoscopy-audit"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy_audit" requiredLevel="standard">
                      <EndoscopyAuditPage />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/onecare/tools/endoscopy2"
                element={
                  <ProtectedRoute>
                    <FeatureGate featureCode="endoscopy2" requiredLevel="standard">
                      <Endoscopy2Page />
                    </FeatureGate>
                  </ProtectedRoute>
                }
              />
              <Route path="/onecare/tools/data-analysis" element={<DataAnalysisPage />} />
              <Route path="/onecare/tasks" element={<ProtectedRoute><TasksPage /></ProtectedRoute>} />
              <Route path="/onecare/notifications" element={<ProtectedRoute><NotificationManagementPageWrapper /></ProtectedRoute>} />
              <Route path="/onecare/staff/patients" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse']}><StaffPatientsPage /></ProtectedRoute>} />
              <Route path="/onecare/staff/feedback" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse', 'Patient Feedback Specialist', 'Patient Experience Manager', 'Patient Relations Coordinator', 'Patient Satisfaction Analyst', 'Complaint Resolution Specialist', 'Patient Advocate', 'Patient Experience', 'PE_ADV']} allowAdmin={true}><StaffFeedbackPage /></ProtectedRoute>} />
              <Route path="/onecare/staff/dashboard" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse', 'Patient Feedback Specialist', 'Patient Experience Manager', 'Patient Relations Coordinator', 'Patient Satisfaction Analyst', 'Complaint Resolution Specialist', 'Patient Advocate', 'Patient Experience', 'PE_ADV']} allowAdmin={true}><StaffDashboardPage /></ProtectedRoute>} />
              <Route path="/onecare/appointments" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse']}><AppointmentsPage /></ProtectedRoute>} />
              <Route path="/onecare/staff" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse']}><StaffDirectoryPage /></ProtectedRoute>} />
              <Route path="/onecare/reports" element={<ProtectedRoute requiredRoles={['Staff', 'Doctor', 'Nurse']}><ReportsPage /></ProtectedRoute>} />
              {/* The open Settings sub-page is part of the URL
                  (/onecare/settings/dashboard). The bare path still works and
                  redirects itself to the default tab. */}
              <Route path="/onecare/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              <Route path="/onecare/settings/:tab" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
              
              {/* Patient Routes - Anonymous Access for Patent-Safe Operation */}
              <Route path="/onecare/patients" element={<PatientWelcomePage />} />
              <Route path="/onecare/patients/ai-chat" element={<AIChatPage />} />
              <Route path="/onecare/patients/services" element={<ServiceInformationPage />} />
              <Route path="/onecare/patients/medical-assistance" element={<MedicalAssistancePage />} />
              <Route path="/onecare/patients/encounter" element={<EncounterNavigationPage />} />
              <Route path="/onecare/patients/encounter-info" element={<EncounterInfoPage />} />
              <Route path="/onecare/patients/feedback" element={<PatientFeedbackPage />} />
              <Route path="/onecare/patients/track" element={<PatientTrackFeedbackPage />} />
              <Route path="/onecare/patients/feedback/track" element={<PatientFeedbackTrackingPage />} />
              <Route path="/onecare/patients/feedback/track/:feedbackId" element={<PatientFeedbackTrackingPage />} />
              
              {/* Admin Routes */}
              <Route
                path="/onecare/admin/*"
                element={
                  <AdminAuthGuard>
                    <AdminRoute requiredLevel="any">
                      <AdminLayout>
                        <Routes>
                        <Route index element={<AdminDashboard />} />
                        <Route 
                          path="global-overview" 
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <GlobalOverviewPage />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="platform" 
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <PlatformAdminDashboard />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="users" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <UserManagement />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="system-health" 
                          element={
                            <AdminRoute requiredLevel="any">
                              <SystemHealth />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="security" 
                          element={
                            <AdminRoute requiredLevel="security">
                              <SecurityCenter />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="hospitals" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <HospitalManagement />
                            </AdminRoute>
                          } 
                        />
                        <Route
                          path="organizations"
                          element={
                            <AdminRoute requiredLevel="system">
                              <OrganizationManagementPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="setup"
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <SetupWizardPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="hospitals-multi"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <HospitalManagementPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="compliance"
                          element={
                            <AdminRoute requiredLevel="organization">
                              <HospitalCompliancePage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="assistant"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <AssistantControlPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="demo-requests"
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <DemoRequestsPage />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="alerts" 
                          element={
                            <AdminRoute requiredLevel="any">
                              <AlertManagement />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="settings" 
                          element={
                            <AdminRoute requiredLevel="system">
                              <SystemSettings />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="theme-control" 
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <ThemeControlPanel />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="ai-permissions" 
                          element={
                            <AdminRoute requiredLevel="system">
                              <AIPermissionControl />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="urgency-permissions"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <UrgencyPermissionControlPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-channels"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <MessagingChannelList />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-channels/new"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <MessagingChannelCreate />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-channels/:channelId/edit"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <MessagingChannelEdit />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-channels/bulk"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <MessagingChannelBulk />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-permissions/superuser"
                          element={
                            <AdminRoute requiredLevel="superuser">
                              <MessagingPermissionsSuperuser />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-permissions/org-admin"
                          element={
                            <AdminRoute requiredLevel="organization">
                              <MessagingPermissionsOrgAdmin />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="messaging-permissions/domain-admin"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <MessagingPermissionsDomainAdmin />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="feature-access/organization"
                          element={
                            <AdminRoute requiredLevel="system">
                              <OrganizationFeatureAccess />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="feature-access/domain"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <DomainFeatureAccess />
                            </AdminRoute>
                          }
                        />
                        <Route 
                          path="notification-triggers" 
                          element={
                            <AdminRoute requiredLevel="system">
                              <NotificationTriggerConfiguration />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="patient-experience" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <PatientExperienceSettings />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="patient-feedback" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <PatientFeedbackDashboard />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="duty-schedules" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <DutyScheduleManagement />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="feedback-management" 
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <FMSControlPanel />
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="notification-sounds" 
                          element={
                            <AdminRoute requiredLevel="system">
                              <Suspense fallback={<div>Loading...</div>}>
                                <AdminSoundControlFixed />
                              </Suspense>
                            </AdminRoute>
                          } 
                        />
                        <Route 
                          path="conductor-analytics" 
                          element={
                            <AdminRoute requiredLevel="any">
                              <ConductorAnalyticsPage />
                            </AdminRoute>
                          } 
                        />
                        <Route
                          path="conductor-settings"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <Suspense fallback={<div className="flex min-h-screen items-center justify-center"><LoadingSpinner size="lg" /></div>}>
                                <ConductorSettingsGate />
                              </Suspense>
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="hospital-conductor-balances"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <HospitalConductorBalancesPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="smart-flow-settings"
                          element={
                            <AdminRoute requiredLevel="any">
                              <SmartFlowSettingsPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="smart-flow-balances"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <SmartFlowHospitalBalancesPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="ai-management"
                          element={
                            <AdminRoute requiredLevel="any">
                              <AIManagementPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="audit-logs"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <AuditReviewPage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="ai-governance"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <AIGovernancePage />
                            </AdminRoute>
                          }
                        />
                        <Route
                          path="identity-vault"
                          element={
                            <AdminRoute requiredLevel="hospital">
                              <IdentityVaultPage />
                            </AdminRoute>
                          }
                        />
                      </Routes>
                      </AdminLayout>
                    </AdminRoute>
                  </AdminAuthGuard>
                }
              />
              
              {/* Unauthorized access page */}
              <Route path="/onecare/unauthorized" element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                  <div className="max-w-md w-full space-y-8 text-center">
                    <div>
                      <h2 className="mt-6 text-3xl font-extrabold text-gray-900">Access Denied</h2>
                      <p className="mt-2 text-sm text-gray-900">
                        You don't have permission to access this area.
                      </p>
                    </div>
                    <div>
                      <button
                        onClick={() => window.history.back()}
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                      >
                        Go Back
                      </button>
                    </div>
                  </div>
                </div>
              } />
              
              <Route path="*" element={<Navigate to="/onecare/" replace />} />
            </Routes>
          </Suspense>
          </VaultProvider>
          </SessionTimeoutProvider>

          {/* Global Floating Chat Widget */}
          <FloatingChatWidget />

          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
            }}
          />
      </div>
    </div>
    </>
  );
}

function App() {
  // SECURITY: The ChatbotSessionProvider holds AI chat sessions/messages
  // (`sessions`, `currentSession`) in local React state. Because logout→login
  // are soft React-Router navigations (no full page reload), a provider instance
  // could otherwise outlive the user it was mounted for — and if a soft login
  // keeps validationState==='valid' while only swapping the user identity, a
  // brief render before the provider's self-clear effect commits could leak the
  // prior user's currentSession.messages. Keying the provider on the
  // authenticated user's id forces React to unmount + remount it (throwing away
  // ALL local chat state) the instant the session's user changes. This is
  // complementary to the provider's existing self-clear effect.
  const currentUserId = useAppSelector((state) => state.auth.user?.id ?? null);
  const chatbotKey = currentUserId != null ? `ai-user-${currentUserId}` : 'ai-anon';
  return (
    <HelmetProvider>
      <ZoomProvider>
        <AuthInitializer>
          <ChatSessionProvider>
            <ChatbotSessionProvider key={chatbotKey}>
              <AppContent />
            </ChatbotSessionProvider>
          </ChatSessionProvider>
        </AuthInitializer>
      </ZoomProvider>
    </HelmetProvider>
  );
}

export default App;
